import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Global single-active-editor lock                                    */
/* ------------------------------------------------------------------ */
// Only one <EditableText> can be in edit mode across the whole page. Other
// instances render as disabled (greyed-out trigger, no click). Using a tiny
// module-scoped pub/sub avoids threading context providers through every page.
let _activeId = null;
let _nextId = 0;
const _listeners = new Set();
const _notify = () => _listeners.forEach((l) => l());
export const editingStore = {
  subscribe: (cb) => {
    _listeners.add(cb);
    return () => _listeners.delete(cb);
  },
  getSnapshot: () => _activeId,
  acquire: (id) => { if (_activeId !== id) { _activeId = id; _notify(); } },
  release: (id) => { if (_activeId === id) { _activeId = null; _notify(); } },
  makeId: () => `edit-${++_nextId}`,
};

const EditableText = ({ value, onSave, multiline = false, type = 'text', prefix = '', className = '', placeholder = 'Click to edit', 'data-testid': dataTestId }) => {
  const [myId] = useState(() => editingStore.makeId());
  const activeId = useSyncExternalStore(editingStore.subscribe, editingStore.getSnapshot);
  const editing = activeId === myId;
  const locked = activeId !== null && !editing;

  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  // Release the lock if this instance unmounts while editing.
  useEffect(() => () => editingStore.release(myId), [myId]);

  const startEdit = () => {
    if (locked) return;
    setDraft(value ?? '');
    editingStore.acquire(myId);
  };

  const cancel = () => {
    setDraft(value ?? '');
    editingStore.release(myId);
  };

  const save = async () => {
    if (draft === (value ?? '')) { editingStore.release(myId); return; }
    setSaving(true);
    try {
      const parsed = type === 'number' ? (draft === '' ? null : Number(draft)) : draft;
      await onSave(parsed);
      editingStore.release(myId);
    } catch (e) { /* toast handled upstream */ }
    finally { setSaving(false); }
  };

  const InputTag = multiline ? 'textarea' : 'input';

  return (
    <div className="relative inline-block w-full" data-testid={dataTestId}>
      {/* Static trigger (always mounted so the layout doesn't jump when editing starts) */}
      <button
        type="button"
        onClick={startEdit}
        disabled={locked}
        aria-label={editing ? 'Editing' : `Edit ${placeholder}`}
        data-testid="editable-trigger"
        className={`group text-left w-full inline-flex items-start gap-1 rounded px-1 py-0.5 -mx-1 transition-colors ${locked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-orange-50 cursor-text'} ${editing ? 'invisible' : ''} ${className}`}
      >
        <span className={value == null || value === '' ? 'text-neutral-400 italic' : ''}>
          {prefix}{value == null || value === '' ? placeholder : String(value)}
        </span>
        {!locked && <Pencil className="w-3 h-3 text-neutral-400 opacity-0 group-hover:opacity-100 mt-1 flex-shrink-0" />}
      </button>

      {editing && (
        <div
          className={`absolute z-30 left-0 top-0 flex items-start gap-1 bg-white border border-[#F46A25] rounded-sm shadow-lg p-1 min-w-full ${multiline ? 'w-[280px]' : 'w-[240px]'}`}
          data-testid="editable-popover"
        >
          <InputTag
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (!multiline && e.key === 'Enter') { e.preventDefault(); save(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            autoFocus
            rows={multiline ? 3 : undefined}
            className={`flex-1 border border-neutral-200 rounded-sm px-2 py-1 text-sm focus:outline-none focus:border-[#F46A25] ${className}`}
          />
          <button
            onClick={save}
            disabled={saving}
            className="p-1 text-green-600 hover:bg-green-50 rounded flex-shrink-0"
            aria-label="Save"
            data-testid="editable-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button
            onClick={cancel}
            className="p-1 text-red-600 hover:bg-red-50 rounded flex-shrink-0"
            aria-label="Cancel"
            data-testid="editable-cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EditableText;
